import clsx from 'clsx'
import { orderBy } from 'lodash-es'

import { FvDateDisplay } from '@fv/client-components'
import type { AdminUserSearchResult, NonAdminUserType } from '@fv/models/admin'
import type { DTO } from '@fv/models/core'

import { AdminBadge } from '@/components/shared/AdminBadge'
import { AdminButton } from '@/components/shared/AdminButton'
import { AdminLink } from '@/components/shared/AdminLink'
import { AdminTable } from '@/components/shared/AdminTable'
import { ViewAsUserLink } from '@/components/shared/ViewAsUserLink'

import { ChangeCarrierSliderButton } from '../carrier-users/ChangeCarrierSliderButton'
import {
  useRemoveUser,
  useUnlockUser,
  useVerifyUser,
} from '../carrier-users/mutations'
import { NO_EXPORT_ATTR } from '../reports/helpers'
import {
  useClearAccountUserSessions,
  useClearUserSessions,
} from '../sessions/api'

type Props = {
  users: DTO<AdminUserSearchResult>[]
  sortable?: boolean
}

const userTypeBadgeClasses: Record<string, string> = {
  carrier: 'badge-primary',
  shipper: 'badge-secondary',
  vendor: 'badge-accent',
}

export const UsersTable = ({ users, sortable }: Props) => {
  const removeUser = useRemoveUser()
  const verifyUser = useVerifyUser()
  const unlockUser = useUnlockUser()
  const clearCarrierUserSessions = useClearUserSessions()
  const clearAccountUserSessions = useClearAccountUserSessions()

  const handleVerify = async (userId: string) => {
    await verifyUser.mutateAsync(userId)
  }

  const handleClearSessions = async (
    userId: string,
    userType: NonAdminUserType,
  ) => {
    if (
      window.confirm(
        'Are you sure you want to clear all sessions? This will sign the user out of all devices.',
      )
    ) {
      if (userType === 'carrier') {
        await clearCarrierUserSessions.mutateAsync(userId)
      } else if (userType === 'shipper' || userType === 'vendor') {
        await clearAccountUserSessions.mutateAsync(userId)
      }
    }
  }

  const handleUnlock = async (userId: string) => {
    await unlockUser.mutateAsync(userId)
  }

  const handleRemove = async (userId: string) => {
    if (
      window.confirm(
        `Are you sure you want to remove this user? This will change their email address to a random one - and they will be removed as a spot quote contact from all associated accounts.  
        
This action cannot be undone.`,
      )
    ) {
      await removeUser.mutateAsync(userId)
    }
  }

  if (!users?.length) return null

  return (
    <AdminTable
      rowKey={u => u._id}
      rowClassName={() => 'leading-snug [&>td]:align-top'}
      data={users}
      disableSort={!sortable}
      columns={[
        {
          key: 'actions',
          label: '',
          render: user => (
            <div
              className="w-[155px] flex gap-2"
              {...{ [NO_EXPORT_ATTR]: true }}
            >
              {user.userType === 'carrier' && !user.isVerified && (
                <AdminButton
                  onClick={() => handleVerify(user._id)}
                  className="btn-success btn-xs"
                  icon="user-check"
                  loading={verifyUser.isPending}
                  tooltip="Verify user"
                />
              )}

              {user.userType === 'carrier' && user.carrier && (
                <ChangeCarrierSliderButton user={user} />
              )}

              <AdminButton
                icon="ban"
                className="btn-xs btn-warning"
                onClick={() => handleClearSessions(user._id, user.userType)}
                tooltip="Clear sessions"
                loading={
                  clearCarrierUserSessions.isPending ||
                  clearAccountUserSessions.isPending
                }
              />

              {user.disabled && (
                <AdminButton
                  icon="unlock"
                  className="btn-xs btn-accent"
                  onClick={() => handleUnlock(user._id)}
                  loading={unlockUser.isPending}
                  tooltip="Unlock user"
                />
              )}

              {user.userType === 'carrier' && !user.isRemoved && (
                <AdminButton
                  icon="trash-xmark"
                  className="btn-xs btn-error"
                  onClick={() => handleRemove(user._id)}
                  loading={removeUser.isPending}
                  tooltip="Remove user"
                />
              )}
            </div>
          ),
        },
        {
          key: 'userType',
          label: 'Type',
          render: user => (
            <span
              className={clsx(
                'badge badge-sm capitalize',
                userTypeBadgeClasses[user.userType],
              )}
            >
              {user.userType}
            </span>
          ),
        },
        {
          key: 'email',
          label: 'Email',
          render: user => (
            <div className="flex flex-col">
              <div className="max-w-80 text-ellipsis overflow-hidden whitespace-nowrap flex items-center gap-2">
                {user.email}
                {user.isRemoved && (
                  <AdminBadge className="badge-error badge-xs">
                    Removed
                  </AdminBadge>
                )}
              </div>

              <small {...{ [NO_EXPORT_ATTR]: true }}>
                {user.profile?.firstName} {user.profile?.lastName}{' '}
                {user.profile?.role ? `| ${user.profile.role}` : ''}
              </small>
              <small {...{ [NO_EXPORT_ATTR]: true }}>{user._id}</small>
              <div className="flex justify-between">
                {user.userType === 'carrier' && (
                  <ViewAsUserLink
                    type="carrier"
                    className="text-xs"
                    userId={user._id}
                    username={user.email}
                  />
                )}
                {user.userType === 'shipper' && user.accountId && (
                  <ViewAsUserLink
                    type="shipper"
                    className="text-xs"
                    accountId={user.accountId}
                    userId={user._id}
                    username={user.email}
                  >
                    View as
                  </ViewAsUserLink>
                )}
                {user.userType === 'vendor' && !!user.vendors?.length && (
                  <ViewAsUserLink
                    type="shipper"
                    className="text-xs"
                    accountId={user.vendors[0].accountId.toString()}
                    userId={user.vendors[0].accountUserId}
                    username={user.email}
                  >
                    View as
                  </ViewAsUserLink>
                )}
              </div>
            </div>
          ),
        },
        {
          key: 'tenant',
          label: 'Tenant',
          render: user => {
            if (user.userType === 'carrier' && user.carrier) {
              return (
                <div className="flex flex-col">
                  <div>
                    <AdminLink to={`/carriers/${user.carrier._id}/overview`}>
                      {user.carrier.name}
                    </AdminLink>
                  </div>
                  <small>{user.carrier._id}</small>
                  <div className="flex justify-between">
                    {user.carrier.code && (
                      <span className="badge badge-sm badge-outline">
                        {user.carrier.code}
                      </span>
                    )}
                  </div>
                </div>
              )
            }

            if (user.userType === 'shipper' && user.accountId) {
              return (
                <div className="flex flex-col">
                  <AdminLink to={`/accounts/${user.accountId}/overview`}>
                    {user.accountName}
                  </AdminLink>
                  <small>{user.accountId}</small>
                  <div className="flex items-center gap-2 mt-1">
                    {user.owner && (
                      <span className="badge badge-sm badge-info">Owner</span>
                    )}
                    <AdminLink
                      to={`/accounts/${user.accountId}/users#${user._id}`}
                      className="text-xs"
                      icon="link"
                    >
                      View in account
                    </AdminLink>
                  </div>
                </div>
              )
            }

            if (user.userType === 'vendor' && user.vendors?.length) {
              return user.vendors.map(v => (
                <div key={v.accountUserId} className="flex flex-col">
                  <AdminLink to={`/vendors/${v.vendorId}/users`}>
                    {v.name}
                  </AdminLink>
                  <small>{v.vendorId}</small>
                  <AdminLink
                    to={`/vendors/${v.vendorId}/users#${user._id}`}
                    className="text-xs"
                    icon="link"
                  >
                    View in vendor
                  </AdminLink>
                </div>
              ))
            }

            return null
          },
        },
        {
          key: 'accounts',
          label: 'Shipper Accounts',
          render: user => {
            return (
              <>
                {(user.accounts ?? [])
                  .map<React.ReactNode>(a => (
                    <>
                      <AdminLink to={`/accounts/${a?._id}/overview`}>
                        {a?.name}
                      </AdminLink>
                    </>
                  ))
                  .reduce<Array<React.ReactNode>>(
                    (prev, curr) =>
                      prev.length === 0 ? [curr] : [...prev, ', ', curr],
                    [],
                  )}
              </>
            )
          },
        },
        {
          key: 'created',
          label: 'Created',
          render: user => (
            <FvDateDisplay utc val={user.created} format="MM/DD/YYYY hh:mm A" />
          ),
        },
        {
          key: 'lastActivity',
          label: 'Last activity',
          sort: (users, dir) => orderBy(users, u => u.lastActivity, dir),
          render: user => (
            <div className="flex flex-col">
              <FvDateDisplay
                utc
                val={user.lastActivity}
                format="MM/DD/YYYY hh:mm A"
              />
            </div>
          ),
        },
      ]}
    />
  )
}
